import { useState } from "react";
import * as ROUTES from "../../../Constants/Routes";
// Translation Package
import { useTranslation } from "react-i18next";

import redirectAfterLoginSuccess from "Components/Auth/functions/redirectAfterLoginSuccess";
import { EMAIL_PATTERN } from "../constants";

// Packages
import firebase from "firebase/compat/app";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

// Components
import PrimaryButton from "../../Buttons/Primary";
import FormInput from "../../Form/FormInput";
import Loading from "../../Utils/Loading";
import SocialMethods from "../Helpers/SocialMethods";
import { DEFAULT_ERROR, handleLoginError } from "./errorHandler";

// Providers
import {
  MODALS,
  useModal,
  useUpdateModal,
} from "../../../Providers/FloatingModalProvider";
import { useUser } from "../../../Providers/UserProvider";
import logGaEvent from "../../../lib/ga/logGaEvent";

// Functions
import clientV2 from "lib/apollo-v2";
import { useSignInContactMutation } from "lib/apollo-v2/generated/graphql";
import trimWhiteSpace from "../../Utils/validate";

// Social sign-in (for provider-aware login view)
import { AiFillApple } from "react-icons/ai";
import { FcGoogle } from "react-icons/fc";
import { PROVIDERS } from "../functions/getProviderId";
import useSocialMethodsSignIn from "../hooks/useSocialMethodsSignIn";

/**
 * Provider-aware login view shown when we know the user's sign-in method.
 * Displays a readonly email field and only the relevant provider button.
 */
const ProviderLoginView: React.FC<{
  email: string;
  signInMethods: string[];
  onSuccess?: () => void;
}> = ({ email, signInMethods, onSuccess }) => {
  const { t } = useTranslation();
  const updateModal = useUpdateModal();
  const signIn = useSocialMethodsSignIn();
  const { refreshUser } = useUser();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * Handle social provider sign-in and invoke onSuccess on completion.
   */
  const handleProviderSignIn = async (provider: firebase.auth.AuthProvider) => {
    setError(null);
    setLoading(true);
    try {
      const jwtToken = await signIn(provider, false);
      if (jwtToken) {
        if (onSuccess) {
          updateModal({ state: false });
          await refreshUser();
          onSuccess();
        } else {
          window.location.reload();
        }
      }
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError?.code === "auth/cancelled-popup-request") return;
      if (firebaseError?.code === "auth/popup-closed-by-user") return;
      setError(firebaseError?.message ?? t("error_general"));
    } finally {
      setLoading(false);
    }
  };

  const showGoogle = signInMethods.includes("google.com");
  const showApple = signInMethods.includes("apple.com");

  return (
    <div className="auth-form">
      <div className="auth-form__login">
        <div className="row">
          <div className="col-12">
            <h6 className="auth-form__form-title">{t("login")}</h6>
          </div>
        </div>

        {/* Readonly email so user knows which account to use */}
        <div className="row">
          <div className="col-12">
            <FormInput
              type="email"
              name="email"
              label={t("email")}
              value={email}
              disabled
            />
          </div>
        </div>

        {error && (
          <div className="row">
            <div className="col-12 auth-form__error">{error}</div>
          </div>
        )}

        {/* Show only the relevant provider button */}
        <div className="row auth-form__social-methods mt-3">
          {showGoogle && (
            <div className="col-12 social-methods__method google">
              <button
                type="button"
                disabled={loading}
                onClick={() => handleProviderSignIn(PROVIDERS.GOOGLE)}
              >
                <span>
                  <FcGoogle className="icon" />
                </span>
                <span>
                  {loading ? t("loading") : `${t("login_with")} ${t("google")}`}
                </span>
              </button>
            </div>
          )}
          {showApple && (
            <div className="col-12 social-methods__method apple">
              <button
                type="button"
                disabled={loading}
                onClick={() => handleProviderSignIn(PROVIDERS.APPLE)}
              >
                <span>
                  <AiFillApple className="icon" />
                </span>
                <span>
                  {loading ? t("loading") : `${t("login_with")} ${t("apple")}`}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LoginForm = (formProps) => {
  const { redirectTo, urlParams, isRoute, routeStateEmail } = formProps;

  //Translation hook
  const { t } = useTranslation();

  const history = useHistory();

  const [email, setEmail] = useState(routeStateEmail);
  const updateEmailValue = (e) => setEmail(e.target.value);

  const { props } = useModal();
  const updateModal = useUpdateModal();
  const { refreshUser } = useUser();

  /** Optional callback invoked after successful login instead of page reload/redirect */
  const onSuccess = props?.onSuccess as (() => void) | undefined;

  /**
   * Provider-aware login: if signInMethods indicates Google or Apple,
   * render the simplified ProviderLoginView instead of the full form.
   */
  const signInMethods: string[] = props?.signInMethods || [];
  const isProviderLogin =
    signInMethods.includes("google.com") || signInMethods.includes("apple.com");

  // All hooks must be called before conditional returns (Rules of Hooks)
  const [error, setError] = useState(DEFAULT_ERROR);
  const [disabled, setDisabled] = useState(false);
  const { register, handleSubmit } = useForm({
    mode: "onBlur",
    defaultValues: {
      email: isRoute ? routeStateEmail : props?.email,
      password: "",
    },
  });

  // Submit form
  const [login] = useSignInContactMutation({
    client: clientV2,
  });

  // Render provider-specific view if applicable (after all hooks)
  if (isProviderLogin && props?.email) {
    return (
      <ProviderLoginView
        email={props.email}
        signInMethods={signInMethods}
        onSuccess={onSuccess}
      />
    );
  }
  const tryLogin = async (data) => {
    setError(DEFAULT_ERROR);
    setDisabled(true);

    await firebase
      .auth()
      .signInWithEmailAndPassword(data.email, data.password)
      .then(async (res) => await res.user.getIdToken())
      .then(async (token) => {
        return await login({
          variables: { firebaseToken: token, payload: {} },
        });
      })
      .then((res) => localStorage.setItem("token", res.data.result.jwtToken))
      .then(() => {
        logGaEvent("login", "email");

        // Wait for GA event
        setTimeout(async () => {
          if (onSuccess) {
            updateModal({ state: false });
            await refreshUser();
            onSuccess();
          } else if (redirectTo) {
            redirectAfterLoginSuccess(redirectTo, urlParams);
          } else {
            history.go(0);
          }
        }, 300);
      })
      .catch((error) => handleLoginError(error, setError));

    setDisabled(false);
  };

  // Modal / page handler
  const onSignupClick = () => {
    if (isRoute) {
      history.push({
        pathname: ROUTES.SIGN_UP,
        search: urlParams,
        state: { email: email },
      });
      return;
    }

    updateModal({
      state: true,
      modal: MODALS.SIGN_UP,
      props: { email: email || null, onSuccess },
    });
    return;
  };

  const onForgotPasswordClick = () => {
    if (isRoute) {
      history.push({
        pathname: ROUTES.FORGOT_PASSWORD,
        search: urlParams,
        state: { email: email },
      });
      return;
    }

    updateModal({
      state: true,
      modal: MODALS.FORGOT_PASSWORD,
      props: { email: email || null, onSuccess },
    });
    return;
  };

  return (
    <div className="auth-form">
      <form onSubmit={handleSubmit(tryLogin)} className="auth-form__login">
        <div className="row">
          <div className="col-12 ">
            <h6 className="auth-form__form-title">{t("login")}</h6>
          </div>
        </div>

        <SocialMethods
          type="login"
          redirectTo={redirectTo}
          urlParams={urlParams}
          onSuccess={onSuccess}
        />

        {/* EMAIL AND PASSWORD */}
        <div className="row gap-4">
          {/* Email address */}
          <div className="col-12 ">
            <FormInput
              register={register("email", {
                required: true,
                pattern: EMAIL_PATTERN,
                validate: (value: any) => trimWhiteSpace()(value),
              })}
              type="email"
              name="email"
              label={t("email")}
              onChange={updateEmailValue}
              onFocus={() => setDisabled(false)}
              autoFocus={true}
            />
          </div>

          {/* Password */}
          <div className="col-12 ">
            <FormInput
              register={register("password", {
                required: true,
              })}
              type="password"
              name="password"
              label={t("password")}
              onFocus={() => setDisabled(false)}
            />
          </div>

          {error.error && (
            <div className="col-12 auth-form__error">
              {error.error && t(error.message)}
            </div>
          )}

          {/* Forgotten password */}
          <div className="col-12 forgot-password">
            <button
              type="button"
              className="auth-form__link"
              onClick={onForgotPasswordClick}
            >
              {t("forgot_password")}
            </button>
          </div>

          <div className="col-12">
            <div className="login-button mt-1">
              <PrimaryButton
                label={disabled ? <Loading light /> : t("login")}
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        {/* OR SIGN UP */}
        <div className="row sign-up">
          <div className="col-12 mt-3 text-center">
            {t("sign_up_question")}
            <button
              className="auth-form__link px-1"
              onClick={onSignupClick}
              data-test-id="sign-up-btn"
              type="button"
            >
              {t("auth_create_account")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
